/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable react/jsx-key */
import React, { FC, useEffect, useRef, useState, memo } from 'react'
import _ from 'lodash'
import { Switch, Checkbox, EmptyData, Button, Input } from '@intelie/ui'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'

import i18n from 'live/services/i18n'
import LiveSelect from 'live/components/forms/ui/select'
import FormGroup from 'live/components/forms/ui/form-group'

import {
    checkLabelAlreadyExists,
    convertSchemaVersion,
    countElements,
    createUUID,
    findElementById,
    findElementIndexById,
    toCamelCase,
    toUpperCase
} from '../utils'
import { useDimensions } from '../../hooks/useDimensions'
import { BuilderCheckboxContainer } from '../../template/styles'
import { RigSelector } from '../selectors/RigSelector'
import { WellSelector } from '../selectors/WellSelector'
import { CrewSelector } from '../selectors/CrewSelector'
import {
    Container,
    EditorAssetsContainer,
    EditorElementContainer,
    ElementList,
    HorizontalGroup,
    MenuContainer,
    StyledButton,
    StyledList,
    SwitchContainer
} from '../styles'
import CustomDatePicker from './CustomDatePicker'
import { ElementLabelInput } from './ElementLabelInput'

const ELEMENT_GROUPS = {
    'text & info': [
        {
            tag: 'text',
            name: 'Input',
            icon: ''
        },
        {
            tag: 'textarea',
            name: 'Free text area',
            icon: ''
        }
    ],
    choice: [
        {
            tag: 'asset',
            name: 'Assets',
            icon: ''
        },
        {
            tag: 'select',
            name: 'Pick list',
            icon: ''
        },
        {
            tag: 'checkbox',
            name: 'Checkbox',
            icon: ''
        }
    ],
    other: [
        {
            tag: 'number',
            name: 'Number',
            icon: ''
        },
        {
            tag: 'date',
            name: 'Date',
            icon: ''
        },
        {
            tag: 'datetime-local',
            name: 'Date & Time',
            icon: ''
        }
    ]
}

const ASSETS_TYPES = [
    {
        type: 'rig',
        name: i18n('Rig')
    },
    {
        type: 'well',
        name: i18n('Well')
    },
    {
        type: 'crew',
        name: i18n('Crew')
    }
]

interface FormBuilderProps {
    formRef?: React.MutableRefObject<FormSchema | null>
    schema?: FormSchema
}

const FormBuilder: FC<FormBuilderProps> = ({ formRef, schema }: FormBuilderProps) => {
    const [form, setForm] = useState<FormSchema>(schema)
    const [elements, setElements] = useState<FormElement[]>([])
    const [elementToEdit, setElementToEdit] = useState<FormElement | undefined>()
    const [errors, setErrors] = useState<Record<string, unknown>[]>([])

    const newOptionRef = useRef<HTMLInputElement | null>(null)
    const counter = useRef(countElements(elements))
    formRef.current = form

    const dimensions = useDimensions({ height: 193 })

    const innerDimensions = React.useMemo(() => {
        return { height: dimensions.height }
    }, [dimensions])

    useEffect(() => {
        const convertedSchema = convertSchemaVersion(schema)

        setForm(convertedSchema)
        setElements(convertedSchema.elements || [])
    }, [schema])

    useEffect(() => {
        setForm(prevState => ({ ...prevState, elements: elements }))
        counter.current = countElements(elements)
    }, [elements])

    const addOptionToSelector = (e): void => {
        e.preventDefault()
        const newOption = newOptionRef?.current?.value

        if (elementToEdit && newOption && newOption.trim() != '') {
            const newOptions = [
                ...(elementToEdit.props as ListProps).options,
                { value: newOption.trim(), label: newOption.trim() }
            ]
            updateElementProp('options', newOptions)
            newOptionRef.current.value = ''
        }
    }

    const addElementToForm = (elementType: string): void => {
        const element = createElement(elementType)

        if (element) {
            setElements(elements => [...elements, element])
        }
    }

    const onLabelInputBlur = (e): void => {
        e.preventDefault()
        const label = e.target.value
        const check = checkLabelAlreadyExists(label, elementToEdit, elements)
        if (check.alreadyExists) {
            setErrors(prevState => [
                ...prevState,
                {
                    type: 'labelAlreadyExists',
                    props: {
                        label: label,
                        elements: check.elements
                    }
                }
            ])
        } else {
            setErrors(prevState =>
                // removes the previously errors of that label if they no longer occurs
                [
                    ...prevState.filter(error => {
                        return error.type === 'labelAlreadyExists' && error.props?.label === label
                    })
                ]
            )
        }
    }

    const isAppendable = (element: FormElement) => {
        return !(element.type === 'group' && element.children.length >= 3)
    }

    const buildHTMLFromFormElement = (element: FormElement): JSX.Element => {
        const elementActions =
            element.parent != undefined ? (
                <>
                    <StyledButton onClick={() => swipeElement(element, 'left')}>
                        <i className="fa fa-angle-left" aria-hidden="true"></i>
                    </StyledButton>
                    <StyledButton onClick={() => swipeElement(element, 'right')}>
                        <i className="fa fa-angle-right" aria-hidden="true"></i>
                    </StyledButton>
                    <StyledButton onClick={() => removeElement(element)} color="var(--red-500)">
                        <i className="fa fa-trash-alt"></i>
                    </StyledButton>
                </>
            ) : null

        switch (element.type) {
            case 'text':
                return (
                    <EditorElementContainer
                        onClick={() => setElementToEdit(element)}
                        focused={element.uuid === elementToEdit?.uuid}
                    >
                        {elementActions}
                        <Input
                            type={element.type}
                            name={element.props.label}
                            disabled={element.props.readOnly}
                            placeholder={element.props.placeholder}
                            label={`${element.props.label} ${element.props.required ? '*' : ''}`}
                            onChange={() => {}}
                        />
                    </EditorElementContainer>
                )
            case 'number':
                return (
                    <EditorElementContainer
                        onClick={() => setElementToEdit(element)}
                        focused={element.uuid === elementToEdit?.uuid}
                    >
                        {elementActions}
                        <Input
                            type={element.type}
                            name={element.props.label}
                            disabled={element.props.readOnly}
                            placeholder={element.props.placeholder}
                            label={`${element.props.label} ${element.props.required ? '*' : ''}`}
                            onChange={() => {}}
                        />
                    </EditorElementContainer>
                )
            case 'date':
                return (
                    <EditorElementContainer
                        onClick={() => setElementToEdit(element)}
                        focused={element.uuid === elementToEdit?.uuid}
                    >
                        {elementActions}
                        <CustomDatePicker
                            name={element.props.label}
                            title={element.props.label}
                            disabled={element.props.readOnly}
                            required={element.props.required}
                            format={element.props.placeholder}
                            onChange={() => {}}
                        />
                    </EditorElementContainer>
                )
            case 'datetime-local':
                return (
                    <EditorElementContainer
                        onClick={() => setElementToEdit(element)}
                        focused={element.uuid === elementToEdit?.uuid}
                    >
                        {elementActions}
                        <CustomDatePicker
                            time
                            name={element.props.label}
                            title={element.props.label}
                            disabled={element.props.readOnly}
                            required={element.props.required}
                            format={element.props.placeholder}
                            onChange={() => {}}
                        />
                    </EditorElementContainer>
                )
            case 'textarea':
                return (
                    <EditorElementContainer
                        onClick={() => setElementToEdit(element)}
                        focused={element.uuid === elementToEdit?.uuid}
                    >
                        {elementActions}
                        <FormGroup
                            componentClass="textarea"
                            label={`${element.props.label} ${element.props.required ? '*' : ''}`}
                            name={element.props.label}
                            placeholder={element.props.placeholder}
                            onChange={() => {}}
                            disabled={element.props.readOnly}
                            className="textarea textarea-md"
                        />
                    </EditorElementContainer>
                )
            case 'select':
                return (
                    <EditorElementContainer
                        onClick={() => setElementToEdit(element)}
                        focused={element.uuid === elementToEdit?.uuid}
                    >
                        {elementActions}
                        <LiveSelect
                            clearable={true}
                            options={(element.props as ListProps).options}
                            label={`${element.props.label} ${element.props.required ? '*' : ''}`}
                            placeholder={element.props.placeholder}
                            onChange={() => {}}
                            disabled={element.props.readOnly}
                        />
                    </EditorElementContainer>
                )
            case 'asset':
                return (
                    <EditorElementContainer
                        onClick={() => setElementToEdit(element)}
                        focused={element.uuid === elementToEdit?.uuid}
                    >
                        {elementActions}
                        {buildAssetSelectorFromElement(element)}
                    </EditorElementContainer>
                )
            case 'checkbox':
                return (
                    <EditorElementContainer
                        onClick={() => setElementToEdit(element)}
                        focused={element.uuid === elementToEdit?.uuid}
                    >
                        {elementActions}
                        <BuilderCheckboxContainer>
                            <Checkbox checked={false} onChange={() => {}} />
                            <label>{`${element.props.label} ${element.props.required ? '*' : ''}`}</label>
                        </BuilderCheckboxContainer>
                    </EditorElementContainer>
                )
            case 'group':
                return (
                    <HorizontalGroup>
                        {element.children?.map((child, idx) => {
                            return buildHTMLFromFormElement(child)
                        })}
                    </HorizontalGroup>
                )
            default:
                return <></>
        }
    }

    const buildAssetSelectorFromElement = (element: FormElement) => {
        const selectorProps = {
            label: element.props.label,
            placeholder: element.props.placeholder,
            required: element.props.required,
            readOnly: element.props.readOnly
        } as AssetSelectorBaseProps

        switch ((element.props as AssetProps).assetType) {
            case 'rig':
                return <RigSelector {...selectorProps} />
            case 'well':
                return <WellSelector {...selectorProps} />
            case 'crew':
                return <CrewSelector {...selectorProps} />
            default:
                return <></>
        }
    }

    const buildPropertiesPanel = (): JSX.Element => {
        if (!elementToEdit) {
            return (
                <div style={{ height: '50vh' }}>
                    <EmptyData title={i18n('No element selected')} icon="far fa-folder-open" />
                </div>
            )
        }

        const isReadOnly = elementToEdit.props.readOnly
        const isRequired = elementToEdit.props.required

        let elementOptions = []
        Object.values(ELEMENT_GROUPS).forEach(group => {
            group.forEach(it => elementOptions.push({ value: it.tag, label: it.name }))
        })

        if (elementToEdit.parent != undefined) {
            elementOptions = elementOptions.filter(op => op.value != 'textarea')
        }

        const assetsOptions = ASSETS_TYPES.map(({ type, name }) => {
            return { value: type, label: name }
        })

        const labelErrors = errors.filter(error => error.type === 'labelAlreadyExists')

        return (
            <div>
                <LiveSelect
                    clearable={false}
                    options={elementOptions}
                    value={elementToEdit.type}
                    label={i18n('Element')}
                    placeholder={i18n('Select an element')}
                    onChange={value => updateElementProp('type', value)}
                    disabled={false}
                />
                <div>
                    <ElementLabelInput
                        value={elementToEdit.props.label}
                        onChange={e => updateElementProp('label', e.target.value)}
                        onBlur={onLabelInputBlur}
                        errors={labelErrors}
                    />
                </div>
                {elementToEdit.type === 'asset' && (
                    <LiveSelect
                        clearable={true}
                        options={assetsOptions}
                        value={(elementToEdit.props as AssetProps).assetType}
                        label={i18n('Asset type')}
                        placeholder={i18n('Select an asset type')}
                        onChange={value => updateElementProp('assetType', value)}
                        disabled={false}
                    />
                )}
                {elementToEdit.type != 'checkbox' && (
                    <>
                        <div>
                            <FormGroup
                                type="text"
                                label="Placeholder"
                                value={elementToEdit.props.placeholder}
                                onChange={e => updateElementProp('placeholder', e.target.value)}
                            />
                        </div>
                        <SwitchContainer>
                            <Switch
                                onChange={() => updateElementProp('readOnly', !isReadOnly)}
                                active={isReadOnly}
                                label={i18n('Read only')}
                                disabled={false}
                            />
                        </SwitchContainer>
                        <SwitchContainer>
                            <Switch
                                onChange={() => updateElementProp('required', !isRequired)}
                                active={isRequired}
                                label={i18n('Required')}
                                disabled={false}
                            />
                        </SwitchContainer>
                    </>
                )}
                {elementToEdit.type === 'select' && (
                    <div>
                        <ul>
                            {(elementToEdit.props as ListProps).options?.map((op, index) => {
                                return <li key={index}>{op.label}</li>
                            })}
                        </ul>
                        <input type="text" ref={newOptionRef} id="listOption" />
                        <Button onClick={addOptionToSelector} icon="plus" buttonFit style={{ marginTop: '8px' }}>
                            {i18n('Add item')}
                        </Button>
                    </div>
                )}
            </div>
        )
    }

    const renderOptionsMenu = (): JSX.Element => {
        return (
            <MenuContainer className="col-md-3 form-builder-section border-right">
                <div className="section-header">
                    <h3 className="section-title">{toUpperCase(i18n('Form elements'))}</h3>
                    <div style={{ paddingRight: '16px' }}>{i18n('Click or Drag & drop elements to preview.')}</div>
                </div>
                <ElementList>
                    {Object.entries(ELEMENT_GROUPS).map(group => {
                        return (
                            <StyledList>
                                <li className="group-title">{_.capitalize(i18n(group[0]))}</li>
                                {group[1].map(it => {
                                    return (
                                        <li
                                            onClick={() => addElementToForm(it.tag)}
                                            draggable={true}
                                            onDragStart={e => e.dataTransfer.setData('dragElementType', it.tag)}
                                        >
                                            {i18n(it.name)}
                                        </li>
                                    )
                                })}
                            </StyledList>
                        )
                    })}
                </ElementList>
            </MenuContainer>
        )
    }

    const createElement = (elementType: string): FormElement | undefined => {
        counter.current = counter.current + 1
        const label = `${i18n('Field')} ${counter.current}`
        const uuid = createUUID(elements)
        switch (elementType) {
            case 'text':
            case 'number':
            case 'date':
            case 'datetime-local':
                return {
                    uuid: uuid,
                    name: uuid,
                    type: elementType,
                    props: {
                        label: label,
                        placeholder: i18n('Type...'),
                        readOnly: false,
                        required: false,
                        help: i18n('Help...')
                    } as InputProps
                }
            case 'select':
                return {
                    uuid: uuid,
                    name: uuid,
                    type: elementType,
                    props: {
                        label: label,
                        placeholder: i18n('Select an option'),
                        readOnly: false,
                        required: false,
                        options: []
                    } as ListProps
                }
            case 'asset':
                return {
                    uuid: uuid,
                    name: uuid,
                    type: elementType,
                    props: {
                        label: label,
                        placeholder: i18n('Select an option'),
                        readOnly: false,
                        required: false,
                        assetType: 'rig'
                    } as AssetProps
                }
            case 'textarea':
                return {
                    uuid: uuid,
                    name: uuid,
                    type: elementType,
                    props: {
                        label: label,
                        placeholder: i18n('Type...'),
                        readOnly: false,
                        required: false,
                        maxLength: 500
                    } as TextareaProps
                }
            case 'checkbox':
                return {
                    uuid: uuid,
                    name: uuid,
                    type: elementType,
                    props: {
                        label: label,
                        placeholder: '',
                        readOnly: false,
                        required: false
                    } as BaseProps
                }
            default:
                return undefined
        }
    }

    const updateElementProp = (prop: string, value: string | boolean | SelectOption[]): void => {
        const updated = { ...elementToEdit } as FormElement
        if (prop === 'type') {
            const base = createElement(value as string) as FormElement
            updated.type = value as
                | 'text'
                | 'number'
                | 'date'
                | 'datetime-local'
                | 'textarea'
                | 'select'
                | 'checkbox'
                | 'group'
            updated.props = {
                ...(base.props as BaseProps | InputProps | ListProps | TextareaProps),
                ...updated.props
            }
        } else if (prop === 'assetType') {
            ;(updated.props as AssetProps).assetType = value as 'rig' | 'well' | 'crew'
        } else {
            if (prop === 'label') {
                updated.name = toCamelCase(value as string)
            }
            updated.props[prop] = value
        }

        const updatedElements = [...elements]
        const parent = elementToEdit?.parent

        if (parent) {
            const parentToUpdate: FormElement = { ...findElementById(parent, elements) }
            const elementIndex = findElementIndexById(elementToEdit.uuid, parentToUpdate.children)
            parentToUpdate.children[elementIndex] = updated
            updatedElements.splice(findElementIndexById(parent, updatedElements), 1, parentToUpdate)
        } else {
            updatedElements.splice(findElementIndexById(elementToEdit.uuid, updatedElements), 1, updated)
        }

        setElementToEdit(updated)
        setElements(updatedElements)
    }

    const removeElement = (element: FormElement): void => {
        const updatedElements = [...elements]

        if (element.parent) {
            const updatedGroup = findElementById(element.parent, updatedElements)
            const elementIndex = findElementIndexById(element.uuid, updatedGroup.children)
            updatedGroup.children.splice(elementIndex, 1)

            const groupIndex = findElementIndexById(updatedGroup.uuid, updatedElements)
            updatedElements.splice(groupIndex, 1, updatedGroup)
        } else {
            const elementIndex = findElementIndexById(element.uuid, updatedElements)
            updatedElements.splice(elementIndex, 1)

            if (elementToEdit) {
                if (elementToEdit.uuid === element.uuid) {
                    setElementToEdit(undefined)
                } else {
                    if (elementToEdit.parent) {
                        const updatedParent = findElementById(elementToEdit.parent, updatedElements)
                        if (updatedParent) {
                            setElementToEdit(findElementById(elementToEdit.uuid, updatedParent.children))
                        } else {
                            setElementToEdit(undefined)
                        }
                    } else {
                        setElementToEdit(findElementById(elementToEdit.uuid, updatedElements))
                    }
                }
            }
        }

        setElements(updatedElements)
    }

    const appendElement = (element: FormElement): void => {
        const updatedElements = [...elements]
        const groupIndex = findElementIndexById(element.uuid, updatedElements)

        if (element.children) {
            const updatedGroup = { ...element }
            const children = updatedGroup.children
            updatedGroup.children = [
                ...children,
                { ...(createElement(children[children.length - 1].type) as FormElement), parent: element.uuid }
            ]
            updatedElements.splice(groupIndex, 1, updatedGroup)
        } else {
            const label = `Group ${counter.current}`
            const uuid = toCamelCase(label)
            const newGroup = {
                uuid: uuid,
                name: uuid,
                type: 'group',
                props: {
                    label: label,
                    placeholder: '',
                    readOnly: false
                } as BaseProps
            } as FormElement
            newGroup.children = [
                { ...element, parent: uuid },
                { ...(createElement(element.type) as FormElement), parent: uuid }
            ]
            updatedElements.splice(groupIndex, 1, newGroup)
        }

        setElements(updatedElements)
    }

    const swipeElement = (element: FormElement, direction: 'left' | 'right'): void => {
        const parentGroup = findElementById(element.parent, elements)
        const parentGroupIndex = findElementIndexById(parentGroup.uuid, elements)
        const children = parentGroup.children
        const elementIndex = findElementIndexById(element.uuid, children)

        const canSwipe =
            (direction === 'left' && elementIndex > 0) || (direction === 'right' && elementIndex < children.length - 1)

        if (canSwipe) {
            const swipeIndex = direction === 'left' ? elementIndex - 1 : elementIndex + 1
            const swipedElement = children[swipeIndex]

            children.splice(swipeIndex, 1, element)
            children.splice(elementIndex, 1, swipedElement)

            const updatedElements = [...elements]
            const updatedGroup = { ...parentGroup, children: children }

            updatedElements.splice(parentGroupIndex, 1, updatedGroup)
            setElements(updatedElements)
        } else {
        }
    }

    const onDragEnd = result => {
        if (!result.destination || !result.source) return

        const sourceIndex = result.source.index
        const destinationIndex = result.destination.index

        const updatedElements = [...elements]

        const sourceElement = updatedElements[sourceIndex]
        const destinationElement = updatedElements[destinationIndex]
        updatedElements.splice(sourceIndex, 1, destinationElement)
        updatedElements.splice(destinationIndex, 1, sourceElement)

        setElements(updatedElements)
    }

    return (
        <Container builderBodyHeight={innerDimensions.height}>
            <section className="section">
                <div className="builder-body">
                    {renderOptionsMenu()}
                    <div
                        className="col-md-6 form-builder-section preview"
                        onDrop={e => {
                            e.preventDefault()
                            const elementType = e.dataTransfer.getData('dragElementType')
                            addElementToForm(elementType)
                        }}
                        onDragOver={e => e.preventDefault()}
                    >
                        <div className="section-header">
                            <h3 className="section-title">{toUpperCase(i18n('Form preview'))}</h3>
                            <div>{i18n('This view is for preview only and data editing is not possible.')}</div>
                        </div>
                        <div className="preview-annotation">
                            <HorizontalGroup>
                                <EditorAssetsContainer>
                                    <div className="from-date">
                                        <CustomDatePicker
                                            time
                                            required
                                            disabled
                                            id="fromDate"
                                            name="fromDate"
                                            className="date-picker"
                                            title={i18n('From')}
                                            date={undefined}
                                            onChange={date => {}}
                                        />
                                    </div>
                                </EditorAssetsContainer>
                                <EditorAssetsContainer>
                                    <div className="to-date">
                                        <CustomDatePicker
                                            time
                                            disabled
                                            id="toDate"
                                            name="toDate"
                                            className="date-picker"
                                            title={i18n('To (optional)')}
                                            date={undefined}
                                            onChange={date => {}}
                                        />
                                    </div>
                                </EditorAssetsContainer>
                            </HorizontalGroup>
                            {elements && elements.length > 0 ? (
                                <DragDropContext onDragEnd={onDragEnd}>
                                    <Droppable droppableId="elements">
                                        {provided => (
                                            <ul {...provided.droppableProps} ref={provided.innerRef}>
                                                {elements.map((el, index) => {
                                                    return (
                                                        <Draggable key={el.uuid} draggableId={el.uuid} index={index}>
                                                            {provided => (
                                                                <li
                                                                    key={index}
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                >
                                                                    <HorizontalGroup>
                                                                        <>{buildHTMLFromFormElement(el)}</>
                                                                        {isAppendable(el) && (
                                                                            <StyledButton
                                                                                onClick={() => appendElement(el)}
                                                                                color="var(--blue-500)"
                                                                            >
                                                                                <i className="fa fa-plus"></i>
                                                                            </StyledButton>
                                                                        )}
                                                                        <StyledButton
                                                                            onClick={() => removeElement(el)}
                                                                            color="var(--red-500)"
                                                                        >
                                                                            <i className="fa fa-trash-alt"></i>
                                                                        </StyledButton>
                                                                    </HorizontalGroup>
                                                                </li>
                                                            )}
                                                        </Draggable>
                                                    )
                                                })}
                                                {provided.placeholder}
                                            </ul>
                                        )}
                                    </Droppable>
                                </DragDropContext>
                            ) : (
                                <div style={{ height: '50vh' }}>
                                    <EmptyData title={i18n('No form element added')} icon="far fa-folder-open" />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="col-md-3 form-builder-section properties">
                        <div className="section-header">
                            <h3 className="section-title">{toUpperCase(i18n('Element Properties'))}</h3>
                        </div>
                        {buildPropertiesPanel()}
                    </div>
                </div>
            </section>
        </Container>
    )
}

export default memo(FormBuilder)
